import React, { useState } from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  CircularProgress,
  DialogActions,
  Divider,
  Grid,
  InputLabel
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import SubscriptionsAPI from 'api/faber/SubscriptionsAPI'
import { RoundedButton } from 'common/Button/Button'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import { useFormik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'
import * as yup from 'yup'

import { CustomerDetailsTextField } from '../components/CustomerDetailsTextField'
const headerMap = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email'
}
function UserProfileForm({
  customerRow,
  onSuccessfulFormSubmission,
  handleDialogClose
}: {
  customerRow: SubscriptionsTableRows
  onSuccessfulFormSubmission: () => void
  handleDialogClose: () => void
}) {
  const faberToken = OrbitSession.get('faberToken')
  const [dialogSequence, setDialogSequence] = useState(0)
  const [initialValues, setInitialValues] = useState({
    firstName: customerRow.firstName,
    lastName: customerRow.lastName,
    email: customerRow.userEmail,
    remarks: ''
  })
  const validationSchema = yup.object({
    remarks: yup.string().required('Remarks are required')
  })

  /* Function to update user details*/
  const {
    mutate: updateFaberUserDetails,
    isLoading: isUpdatingUserDetailsLoading
  } = useMutation(SubscriptionsAPI.updateFaberUserDetails, {
    onSuccess: () => {
      setInitialValues({
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        email: formik.values.email,
        remarks: ''
      })
      onSuccessfulFormSubmission()

      toast.success('Customer details updated')
    },
    onError: () => {
      toast.error('Error in updating customer details')
    },
    onSettled: () => {
      handleDialogClose()
    }
  })

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: (values) => {
      updateFaberUserDetails({
        userId: customerRow.userID,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        remarks: values.remarks,
        faberToken: faberToken
      })
    },
    validationSchema: validationSchema
  })
  return (
    <>
      <Box px={4}>
        {dialogSequence === 0 && (
          <form>
            <Box sx={{ my: 1 }}>
              <CustomerDetailsTextField
                name='firstName'
                value={formik.values.firstName}
                onChange={formik.handleChange}
                id='firstName'
                variant='standard'
                multiline
                maxRows={3}
                sx={{ minWidth: '500px', resize: 'horizontal', ml: '5px' }}
                InputProps={{
                  sx: {
                    color: '#333',
                    fontSize: '14px'
                  }
                }}
              />
              <InputLabel sx={{ ml: '5px', fontSize: '12px' }}>
                First Name
              </InputLabel>
            </Box>
            <Box sx={{ my: 1 }}>
              <CustomerDetailsTextField
                name='lastName'
                value={formik.values.lastName}
                onChange={(e) => {
                  formik.handleBlur(e)
                  formik.handleChange(e)
                }}
                id='lastName'
                variant='standard'
                multiline
                maxRows={3}
                sx={{ minWidth: '500px', resize: 'horizontal', ml: '5px' }}
                InputProps={{
                  sx: {
                    color: '#333',
                    fontSize: '14px'
                  }
                }}
              />
              <InputLabel sx={{ ml: '5px', fontSize: '12px' }}>
                Last Name
              </InputLabel>
            </Box>
            <Box sx={{ my: 1 }}>
              <CustomerDetailsTextField
                name='email'
                value={formik.values.email}
                onChange={(e) => {
                  formik.handleBlur(e)
                  formik.handleChange(e)
                }}
                id='email'
                variant='standard'
                multiline
                maxRows={3}
                sx={{ minWidth: '500px', resize: 'horizontal', ml: '5px' }}
                InputProps={{
                  sx: {
                    color: '#333',
                    fontSize: '14px'
                  }
                }}
              />
              <InputLabel sx={{ ml: '5px', fontSize: '12px' }}>
                Email
              </InputLabel>
            </Box>
            <Box sx={{ my: 1 }}>
              <CustomerDetailsTextField
                variant='standard'
                id='remarks'
                multiline
                value={formik.values.remarks}
                onChange={(e) => {
                  formik.handleBlur(e)
                  formik.handleChange(e)
                }}
                sx={{ minWidth: '500px', resize: 'horizontal', ml: '5px' }}
                InputProps={{
                  sx: {
                    color: '#333',
                    fontSize: '14px'
                  }
                }}
                error={formik.touched.remarks && Boolean(formik.errors.remarks)}
                helperText={formik.touched.remarks && formik.errors.remarks}
              />
              {!(formik.touched.remarks && Boolean(formik.errors.remarks)) ? (
                <InputLabel sx={{ ml: '5px', fontSize: '12px' }}>
                  Remarks
                </InputLabel>
              ) : null}
            </Box>
            <DialogActions>
              <RoundedButton variant='outlined' onClick={handleDialogClose}>
                Cancel
              </RoundedButton>
              <RoundedButton
                disabled={!formik.values.remarks || !formik.isValid}
                variant='contained'
                onClick={() => setDialogSequence(1)}
                sx={{
                  ':disabled': {
                    border: 'none'
                  }
                }}
              >
                Confirm
              </RoundedButton>
            </DialogActions>
          </form>
        )}
      </Box>
      {dialogSequence === 1 && (
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ px: 4 }}>
            <Grid
              container
              sx={{
                mt: 2,
                height: '5vh',
                padding: '4px 0',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#f6f6f6'
              }}
            >
              <Grid item xs={6}>
                <TextColorFontBody
                  sx={{
                    fontWeight: 'bolder',
                    fontSize: '16px',
                    textAlign: 'left',
                    ml: 1
                  }}
                >
                  Current
                </TextColorFontBody>
              </Grid>
              <Grid item xs={6}>
                <TextColorFontBody
                  sx={{
                    fontWeight: 'bolder',
                    fontSize: '16xpx',
                    textAlign: 'left'
                  }}
                >
                  Changes made
                </TextColorFontBody>
              </Grid>
            </Grid>
            <Grid container px={1} mt={2} mb={1}>
              {Object.keys(headerMap).map((fieldName, index) => {
                return (
                  <React.Fragment key={fieldName}>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        fontWeight: 700,
                        textAlign: 'left',
                        mb: 1,
                        fontSize: '14px'
                      }}
                    >
                      {headerMap[fieldName]}
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        fontWeight: 700,
                        textAlign: 'left',
                        fontSize: '14px'
                      }}
                    >
                      {headerMap[fieldName]}
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        textAlign: 'left',
                        fontSize: '12px',
                        wordBreak: 'break-all'
                      }}
                    >
                      {formik.initialValues[fieldName]}
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      sx={{
                        textAlign: 'left',
                        fontSize: '12px',
                        wordBreak: 'break-all'
                      }}
                    >
                      {formik.values[fieldName]}
                    </Grid>
                    {index !== Object.keys(headerMap).length - 1 && (
                      <Divider sx={{ margin: '8px auto', width: '100%' }} />
                    )}
                  </React.Fragment>
                )
              })}
            </Grid>
            <DialogActions>
              <RoundedButton
                variant='outlined'
                onClick={() => setDialogSequence(0)}
              >
                Back
              </RoundedButton>
              <LoadingButton
                type='submit'
                variant='filled'
                loadingIndicator={
                  <CircularProgress color='inherit' size={16} />
                }
                loading={isUpdatingUserDetailsLoading}
              >
                Confirm
              </LoadingButton>
            </DialogActions>
          </Box>
        </form>
      )}
    </>
  )
}
export default UserProfileForm
